import * as React from 'react';

import {
  type InteractionTrackingEvent,
  type NWTEvent,
  useTracking,
} from '@xing-com/crate-communication-tracking';
import { useLoggedInUser } from '@xing-com/crate-hooks-use-logged-in-user';

export type AdobeTrackingEvent<T = Record<string, unknown>> =
  InteractionTrackingEvent<T>;

type OptionalNWTrackingParams = {
  event: string;
  element?: string;
  element_state?: string;
  itemUrn?: string;
  itemActorUrn?: string;
  moduleTitle?: string;
  position?: number;
  verticalPosition?: number;
  modulePosition?: number;
  positionInModule?: number;
  element_detail?: string;
};

type OverrideAdobeData = {
  trackAction?: string;
  PropActionOrigin?: string;
  pageName?: string;
  event?: string;
  EventProfileEditingSave?: string;
};

type AdobeData = OverrideAdobeData;

type UseInsightsTrackingProps = OverrideAdobeData;

type UseInsightsTrackingReturn = {
  trackWithNWT: (extraData: OptionalNWTrackingParams) => void;
  trackInteraction: (
    overrideTrackAction?: string,
    extraData?: Omit<AdobeData, 'trackAction'>
  ) => void;
};

export type AdobePropTrackActionEvent = AdobeTrackingEvent<
  {
    type: 'interaction';
    event: string;
    PropTrackAction: string;
    PropActionOrigin: string;
  } & Record<string, unknown>
>;

export const getAdobePropTrackActionEvent = (
  trackActionProp: string,
  actionOriginProp: string,
  eventName?: string,
  options?: Record<string, unknown>
): AdobePropTrackActionEvent => ({
  type: 'interaction',
  event: eventName || 'trackAction',
  PropTrackAction: trackActionProp,
  PropActionOrigin: actionOriginProp,
  ...options,
});

export const useInsightsTracking = ({
  trackAction,
  PropActionOrigin,
}: UseInsightsTrackingProps = {}): UseInsightsTrackingReturn => {
  const { track } = useTracking<AdobeTrackingEvent | NWTEvent>();
  const { id: userId } = useLoggedInUser() ?? {};

  const trackWithNWT = React.useCallback<
    UseInsightsTrackingReturn['trackWithNWT']
  >(
    (extraData) => {
      track({
        application: 'wbm',
        eventSchema: 'extended',
        sentBy: 'disco',
        page: 'discover/update',
        type: 'nwt',
        userId,
        ...extraData,
      });
    },
    [track, userId]
  );

  const trackInteraction = React.useCallback<
    UseInsightsTrackingReturn['trackInteraction']
  >(
    (overrideTrackAction, extraData) => {
      const sentTrackAction = overrideTrackAction ?? trackAction;
      const sentActionOrigin = PropActionOrigin || '';
      const sentEventName = extraData?.event || undefined;
      const event =
        sentTrackAction &&
        getAdobePropTrackActionEvent(
          sentTrackAction,
          sentActionOrigin,
          sentEventName,
          {
            ...extraData,
          }
        );
      event && track(event);
    },
    [track, trackAction, PropActionOrigin]
  );

  return {
    trackWithNWT,
    trackInteraction,
  };
};
